import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import axios from 'axios';
import useCustomerStore from './useCustomerStore';
import useReportStore from './useReportStore';

// CariBilgi arayüzüne minimumLimitForInstallment alanı eklendi.
export interface CariBilgi {
  Message: string;
  CariKod: string;
  CariIsim: string;
  Telefon: string;
  Adres: string;
  Ilce: string;
  Il: string;
  Ulke: string;
  VergiDairesi: string;
  VergiNo: string;
  Borc: number;
  Alacak: number;
  Bakiye: number;
  Email: string;
  TaksitSayisi: number;
  minimumLimitForInstallment: number;
  customerName?: string;
}

// Admin bilgilerini temsil eden arayüz.
interface AdminInfo {
  isAdmin: boolean;
  firstName: string | null;
  lastName: string | null;
  customerId: string;
  customerName: string | null;
  minimumLimitForInstallment: number;
  installmentCount: number;
}

// Login isteğinde kullanılacak parametreler.
interface LoginParams {
  userName: string;
  password: string;
  companyId?: string;
}

// API'den dönen yanıtın arayüzü.
interface LoginResponse {
  result: {
    isSuccess: boolean;
    message: string;
    isAdmin: boolean;
    firstName: string | null;
    lastName: string | null;
    customerName: string | null;
    customerId: string ;
    minimumLimitForInstallment: number;
    installmentCount: number | null;
    currentCode: string | null;
    currentName: string | null;
    phone: string | null;
    address: string | null;
    district: string | null;
    city: string | null;
    country: string | null;
    taxOffice: string | null;
    taxNo: string | null;
    debit: number | null;
    credit: number | null;
    balance: number | null;
    email: string | null;
   
  };
}

export interface AuthState {
  user: CariBilgi | null;
  adminInfo: AdminInfo | null;
  amount: string;
  setAmount: (amount: string) => void;
  error: string | null;
  isLoggedIn: boolean;
  selectedInstallment: number | null;
  customerId: string ; // Added customerId to AuthState
  login: (params: LoginParams) => Promise<void>;
  logout: () => void;
  setSelectedInstallment: (installment: number) => void;
  getInstallmentData: () => {
    minimumLimitForInstallment: number;
    installmentCount: number;
  };
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      amount: '',
  setAmount: (amount: string) => set({ amount }),
      adminInfo: null,
      error: null,
      isLoggedIn: false,
      selectedInstallment: null,
      customerId: '',

      login: async ({ userName, password, companyId = '' }: LoginParams) => {
        try {
          const loginUrl = 'https://pay.duyu.alter.net.tr/api/Login';
          const formData = new FormData();
          formData.append('username', userName);
          formData.append('password', password);
          if (companyId) {
            formData.append('companyId', companyId);
          }

          const response = await axios.post<LoginResponse>(loginUrl, formData);
          const data = response.data.result;

          if (data.isSuccess) {
            if (data.isAdmin) {
              const adminInfo: AdminInfo = {
                isAdmin: true,
                firstName: data.firstName,
                lastName: data.lastName,
                customerId: data.customerId || '',
                customerName: data.customerName,
                minimumLimitForInstallment: data.minimumLimitForInstallment || 0,
                installmentCount: data.installmentCount || 0,
              };
              set({
                user: null,
                adminInfo,
                isLoggedIn: true,
                error: null,
                customerId: data.customerId || '', 
              });
            } else {
              // Non-admin kullanıcılar için minimumLimitForInstallment bilgisini de user objesine ekledik.
              const user: CariBilgi = {
                Message: data.message,
                CariKod: data.currentCode || '',
                CariIsim: data.currentName || '',
                Telefon: data.phone || '',
                Adres: data.address || '',
                Ilce: data.district || '',
                Il: data.city || '',
                Ulke: data.country || '',
                VergiDairesi: data.taxOffice || '',
                VergiNo: data.taxNo || '',
                Borc: data.debit || 0,
                Alacak: data.credit || 0,
                Bakiye: data.balance || 0,
                Email: data.email || '',
                TaksitSayisi: data.installmentCount || 0,
                minimumLimitForInstallment: data.minimumLimitForInstallment || 0,
                customerName: data.customerName || '',
              };
              set({
                user,
                adminInfo: null,
                isLoggedIn: true,
                error: null,
                customerId: data.customerId || '', 
              });
              
            }
          } else {
            set({
              user: null,
              adminInfo: null,
              isLoggedIn: false,
              error: data.message || 'Giriş başarısız.',
            });
          }
        } catch (err: any) {
          set({
            user: null,
            adminInfo: null,
            isLoggedIn: false,
            error: err?.message || 'Sunucu hatası.',
          });
        }
      },

      logout: () => {
        set({
          user: null,
          adminInfo: null,
          error: null,
          isLoggedIn: false,
          amount: '',
          selectedInstallment: null,
        });
        useCustomerStore.setState({
          selectedCustomer: null,
          customers: [], // ✔ boşalt
          customerDetail: null,
          error: null,
        
        
          
        })

        useReportStore.setState({
          reportData: [],
          logData: [],
          isLoading: false,
          error: null,
        });
        sessionStorage.removeItem('auth-store');
        sessionStorage.removeItem('customer-store')
      },
      
      setSelectedInstallment: (installment: number) =>
        set({ selectedInstallment: installment }),

      getInstallmentData: () => {
        const { adminInfo, user } = get();
        return {
          // Önce adminInfo üzerinden, yoksa user üzerinden minimumLimitForInstallment okunur.
          minimumLimitForInstallment:
            adminInfo?.minimumLimitForInstallment ?? user?.minimumLimitForInstallment ?? 0,
          installmentCount: adminInfo?.installmentCount ?? user?.TaksitSayisi ?? 1,
        };
      },
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

export default useAuthStore;
